import { ChangeDetectorRef, Component, HostBinding, OnDestroy, OnInit } from '@angular/core';
import { CommonModule, NgFor, NgIf } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { finalize, SubscriptionLike } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { MatButton } from '@angular/material/button';
import { MatFormField, MatInput, MatLabel } from '@angular/material/input';
import { ConsultaList } from 'src/app/shared/model/core/ConsultaList.model';
import { GenericoService } from 'src/app/shared/services/generico.service';
import { GestionclientesService } from '../gestionclientes.service';
import { ModaladduserComponent } from '../modaladduser/modaladduser.component';
import { ShowusuariosmodalComponent } from '../showusuariosmodal/showusuariosmodal.component';
import { EditusuariomodalComponent } from '../editusuariomodal/editusuariomodal.component';
import { UsuarioClienteBean } from 'src/app/shared/model/ajustesUsuario/usuarioClienteBean.model';

@Component({
  selector: 'app-consultar-usuarios',
  standalone: true,
  templateUrl: './consultar-usuarios.component.html',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    NgIf,
    NgFor,
    MatFormField,
    MatLabel,
    MatInput,
    MatButton,
  ],
})
export class ConsultarUsuariosComponent implements OnInit, OnDestroy {
  listaClientes: Array<UsuarioClienteBean> = [];
  shed = new ConsultaList<UsuarioClienteBean>();
  totalRegistros = 0;
  totalPages = 0;
  pageSize = 5;
  readonly pageSizeOptions = [5, 10, 20];
  currentPage = 1;
  loading = false;
  /**
   * Motivo cuando el servidor rechaza la consulta (00002: sin la hoja de
   * Clientes o sin empresa en la sesion). Se muestra en lugar de "No se
   * encontraron usuarios", que haria creer que el cliente no tiene ninguno.
   */
  mensajeError = '';

  @HostBinding('class.is-open')
  isOpen = false;

  regresaPag = false;
  private subscription?: SubscriptionLike;

  constructor(
    private dialog: MatDialog,
    private clientService: GestionclientesService,
    private generico: GenericoService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.subscription = this.generico.isOpen.subscribe((res) => {
      this.regresaPag = res;
    });

    this.shed.bean = new UsuarioClienteBean();
    this.consultaPagina(1);
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
    this.generico.getEstatusPantalla(false);
  }

  mostrarUsuario(usuario: UsuarioClienteBean) {
    this.dialog.open(ShowusuariosmodalComponent, {
      height: '720px',
      width: '800px',
      panelClass: 'custom-modalbox',
      data: usuario,
    });
  }

  abrirModalAgregarUsuario(): void {
    const dialogoRef = this.dialog.open(ModaladduserComponent, {
      disableClose: true,
      autoFocus: true,
      panelClass: 'custom-modalbox',
    });

    dialogoRef.afterClosed().subscribe(() => {
      this.consultaPagina(this.currentPage);
    });
  }

  editarUsuario(usuario?: UsuarioClienteBean): void {
    const dialogRef = this.dialog.open(EditusuariomodalComponent, {
      height: '720px',
      width: '800px',
      panelClass: 'custom-modalbox',
      data: usuario,
    });

    dialogRef.afterClosed().subscribe(() => {
      this.consultaPagina(this.currentPage);
    });
  }

  filtroBusqueda(filtro: string): void {
    this.shed.param1 = filtro?.trim() ? filtro.trim() : null;
    this.consultaPagina(1);
  }

  consultaPagina(page: number): void {
    this.currentPage = page;
    this.loading = this.listaClientes.length === 0;
    this.cdr.detectChanges();
    this.shed.regInicial = (page - 1) * this.pageSize;
    this.shed.regXPagina = this.pageSize;

    this.clientService
      .filtroBusquedaUsuariosCliente(this.shed)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (res) => {
          this.mensajeError =
            res?.pCodRetorno === '00002'
              ? res.pMensaje || 'No tiene permiso para consultar los usuarios de clientes.'
              : '';
          this.totalRegistros = this.resolveTotalRegistros(res);
          this.listaClientes = [...(Array.isArray(res?.list) ? res.list : [])];
          this.totalPages =
            this.totalRegistros > 0
              ? Math.ceil(this.totalRegistros / this.pageSize)
              : 0;

          if (this.totalPages > 0 && this.currentPage > this.totalPages) {
            this.consultaPagina(this.totalPages);
            return;
          }

          this.cdr.markForCheck();
          this.cdr.detectChanges();
        },
        error: () => {
          this.listaClientes = [];
          this.totalRegistros = 0;
          this.totalPages = 0;
          this.mensajeError = '';
          this.cdr.markForCheck();
          this.cdr.detectChanges();
        },
      });
  }

  onPageSizeChange(value: number): void {
    this.pageSize = Number(value);
    this.consultaPagina(1);
  }

  onPageChange(page: number): void {
    if (page === this.currentPage || page < 1 || page > this.totalPages) {
      return;
    }
    this.consultaPagina(page);
  }

  firstPage(): void {
    this.onPageChange(1);
  }

  previousPage(): void {
    this.onPageChange(this.currentPage - 1);
  }

  nextPage(): void {
    this.onPageChange(this.currentPage + 1);
  }

  lastPage(): void {
    this.onPageChange(this.totalPages);
  }

  get showPagination(): boolean {
    return this.totalRegistros > 0;
  }

  get rangeEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalRegistros);
  }

  getPaginationItems(): Array<number | string> {
    if (this.totalPages <= 1) {
      return [1];
    }

    const pages = new Set<number>([
      1,
      this.totalPages,
      this.currentPage - 1,
      this.currentPage,
      this.currentPage + 1,
    ]);
    const sortedPages = Array.from(pages)
      .filter((page) => page >= 1 && page <= this.totalPages)
      .sort((a, b) => a - b);

    const items: Array<number | string> = [];
    for (let index = 0; index < sortedPages.length; index++) {
      const page = sortedPages[index];
      const previous = sortedPages[index - 1];
      if (previous && page - previous > 1) {
        items.push(`ellipsis-${previous}-${page}`);
      }
      items.push(page);
    }

    return items;
  }

  isPageNumber(item: number | string): item is number {
    return typeof item === 'number';
  }

  private resolveTotalRegistros(res: ConsultaList<UsuarioClienteBean>): number {
    const response = res as ConsultaList<UsuarioClienteBean> & Record<string, unknown>;
    const candidates = [
      response?.pTotalItems,
      response?.totalItems,
      response?.totalRegistros,
      response?.param4,
    ];

    for (const candidate of candidates) {
      const parsed = Number(candidate);
      if (Number.isFinite(parsed) && parsed > 0) {
        return parsed;
      }
    }

    const currentItems = Array.isArray(res?.list) ? res.list.length : 0;
    if (currentItems === 0) {
      return 0;
    }

    return (this.currentPage - 1) * this.pageSize + currentItems;
  }
}
